import {
  ChangeEvent,
  forwardRef,
  InputHTMLAttributes,
  useImperativeHandle,
} from 'react';
import { observer } from 'mobx-react-lite';
import ReactInputMask from 'react-input-mask';
import { usePhoneInput } from './use-phone-input';
import { InputProps } from '../types';
import { cross } from 'assets/icons';
import { Message } from '../../components';
import { Container } from '../../common.styled';
import { ActionIcon, InputContainer } from '../inputs.styled';
import { CountryWrapper, CustomizedInput, RuIcon } from './phone-input.styled';

type PhoneInputProps = Omit<InputProps, 'icon'> & {
  handleClear?: () => void;
};

/* Принимает в себя все пропсы BaseInput, подробно описаны в BaseInput.
 * Дополнительно принимает handleClear - функция очистки контролируемого извне значения */
export const PhoneInput = observer(
  forwardRef<HTMLInputElement, PhoneInputProps>(
    (
      {
        state = 'default',
        width = 's',
        message,
        actionIcon,
        handleClear,
        ...inputProps
      },
      forwardedRef
    ) => {
      const { inputRef, onChange } = usePhoneInput(inputProps.onChange);

      useImperativeHandle(forwardedRef, () => inputRef.current!);

      const focusInput = () => {
        inputRef.current?.focus();
      };

      const upgradedHandleClear = () => {
        inputProps.onChange?.({
          ...({} as ChangeEvent<HTMLInputElement>),
          target: { value: '' },
        } as ChangeEvent<HTMLInputElement>);

        handleClear?.();

        // фокус + курсор в начало после перерисовки маски
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(0, 0);
          }
        }, 0);
      };

      const hasValue =
        !!inputProps.value?.toString().match(/[0-9]/g) && !inputProps.disabled;

      return (
        <Container
          $state={state}
          $width={width}
          $disabled={inputProps.disabled || false}
        >
          <InputContainer>
            <CountryWrapper
              onClick={focusInput}
              $isDisabled={inputProps.disabled}
            >
              <RuIcon />
              <span>+7</span>
            </CountryWrapper>
            <ReactInputMask
              {...inputProps}
              mask={'(999) 999-99-99'}
              alwaysShowMask
              onChange={onChange}
            >
              {(
                maskProps: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
              ) => (
                <CustomizedInput
                  ref={inputRef}
                  /* ReactInputMask передаёт не все пропсы по пока неизвестной причине: disabled, например.
                   * Может найдется что-то ещё. */
                  disabled={inputProps.disabled}
                  {...maskProps}
                />
              )}
            </ReactInputMask>

            {/* ReactInputMask пишет значения вида (___) 999-__-99 и тп в value. (___) ___-__-__ по умолчанию */}
            {hasValue && (
              <ActionIcon src={cross} onClick={upgradedHandleClear} />
            )}
          </InputContainer>

          {!!message?.[state] && <Message state={state} message={message} />}
        </Container>
      );
    }
  )
);
